'use client';

import { __spreadArray } from '../../../_virtual/_tslib.js';
import React__default, { useEffect, useRef, useCallback } from 'react';
import { useSubscribeField, useGlobalState } from '../../context.js';
import formErrorsControl from '../../utils/formErrorsControl.js';
import getFieldValidatedMessage from '../../utils/getFieldValidatedMessage.js';
import unproxify from '../../utils/unproxify.js';
import setFormFieldValue from '../../utils/setFormFieldValue.js';
import getScrollableParent from '../../utils/getScrollableParent.js';
import { FormItemContext } from './context.js';

var FormItem = function (_a) {
    var children = _a.children, name = _a.name, rules = _a.rules, watch = _a.watch;
    // Get value
    var watchValue = useSubscribeField("values.".concat(name));
    var watchError = useSubscribeField("errors.".concat(name));
    var watchErrorFields = useSubscribeField('errorFields');
    var isScrollToFirstErrorElement = useSubscribeField('isScrollToFirstErrorElement');
    var watchFieldValue = useSubscribeField("watchValues.".concat(name));
    var globalValues = useSubscribeField('values');
    // Set value
    var formState = useGlobalState();
    var fieldRules = formState.formRules[name];
    useEffect(function () {
        /*
         * Set rules to global state
         * */
        if (!Object.keys(formState.formRules).includes(name) && rules !== undefined) {
            formState.formRules[name] = rules;
        }
        /*
         * Set watch field and value to global state
         * */
        if (watch && watch.length > 0) {
            var watchValues_1 = {};
            formState.watch[name] = watch;
            watch.forEach(function (key) {
                var _a;
                return Object.assign(watchValues_1, (_a = {}, _a[key] = globalValues[key], _a));
            });
            formState.watchValues[name] = watchValues_1;
        }
    }, [watch, name, rules, globalValues]);
    /*
     * Scroll to error action
     * */
    var itemRef = useRef(null);
    var setItemRef = useCallback(function (node) {
        if (itemRef.current !== node) {
            itemRef.current = node;
        }
    }, []);
    useEffect(function () {
        var firstErrorField = watchErrorFields[0];
        if (isScrollToFirstErrorElement && firstErrorField && firstErrorField === name) {
            if (itemRef.current) {
                var scrollableElement = getScrollableParent(itemRef.current);
                var scrollableElementTop = scrollableElement instanceof Window ? 0 : scrollableElement.getBoundingClientRect().top;
                var itemTop = itemRef.current.offsetTop;
                if (firstErrorField === name) {
                    /*
                     * Only scroll to first error if element is off screen
                     * */
                    scrollableElement.scrollTo({
                        top: itemTop - scrollableElementTop,
                        behavior: 'smooth'
                    });
                }
                formState.isScrollToFirstErrorElement = false;
                formState.errorFields = [];
            }
        }
        return function () { return undefined; };
    }, [watchErrorFields, isScrollToFirstErrorElement, name]);
    /*
     * Handle field action
     * */
    var handleTouchedField = function () {
        /*
         * Validate field onBlur
         * */
        if (fieldRules !== undefined) {
            var watchValueFields = formState.watchValues[name];
            var fieldValue = formState.values[name];
            var errorMsg = getFieldValidatedMessage({
                fieldValue: fieldValue,
                fieldRules: fieldRules,
                fieldWatch: watchValueFields,
                isEnable: true
            });
            formErrorsControl({
                fieldName: name,
                formValues: formState,
                errorMsg: errorMsg,
                requestScrollToFirstErrorElement: false
            });
        }
        if (formState.touched.includes(name)) {
            return;
        }
        formState.touched = __spreadArray(__spreadArray([], formState.touched, true), [name], false);
    };
    var handleChangeField = function (value) {
        setFormFieldValue({
            formState: formState,
            field: name,
            value: value,
            rules: fieldRules
        });
    };
    var setFieldValue = function (field, value) {
        setFormFieldValue({
            formState: formState,
            field: field,
            value: value
        });
    };
    return (React__default.createElement(FormItemContext.Provider, { value: {
            rules: fieldRules || [],
            errorMsg: watchError || ''
        } },
        isScrollToFirstErrorElement ? React__default.createElement("div", { ref: setItemRef, id: name }) : null,
        children({
            value: unproxify(watchValue),
            onChange: handleChangeField,
            onBlur: handleTouchedField,
            error: watchError,
            isError: watchError !== undefined && watchError !== '',
            watch: unproxify(watchFieldValue),
            setFieldValue: setFieldValue,
            setItemRef: setItemRef
        })));
};

export { FormItem as default };
//# sourceMappingURL=index.js.map
